import { CheckCircleOutlined, CheckOutlined, CopyOutlined, InfoCircleOutlined, ReloadOutlined, RobotOutlined } from "@ant-design/icons";
import { Alert, Button, Empty, List, Modal, Space, Spin, Typography, message } from "antd";
import { useEffect, useMemo, useState } from "react";

const { Text, Paragraph } = Typography;

const AIDescriptionGenerator = ({ visible, onClose, onSelect, tagName, businessAreaName, generateDescriptionWithAI, aiDescription }) => {
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [localGenerating, setLocalGenerating] = useState(false);
    const [lastGeneratedParams, setLastGeneratedParams] = useState({ tagName: "", businessAreaName: "" });

    const suggestions = useMemo(() => aiDescription || [], [aiDescription]);

    useEffect(() => {
        if (visible && suggestions.length > 0) setLocalGenerating(false);
    }, [visible, suggestions]);

    const hasParamsChanged = useMemo(() => {
        if (suggestions.length === 0) return false;
        return lastGeneratedParams.tagName !== (tagName || "").trim() || lastGeneratedParams.businessAreaName !== (businessAreaName || "").trim();
    }, [suggestions.length, lastGeneratedParams, tagName, businessAreaName]);

    useEffect(() => {
        if (!visible) {
            setLocalGenerating(false);
            setCopiedIndex(null);
        }
    }, [visible]);

    const handleGenerate = () => {
        if (!tagName?.trim()) return;
        setLocalGenerating(true);
        setCopiedIndex(null);
        setLastGeneratedParams({ tagName: tagName.trim(), businessAreaName: (businessAreaName || "").trim() });
        generateDescriptionWithAI?.({
            tagName: tagName.trim(),
            businessAreaName: (businessAreaName || "").trim(),
        });
    };

    const handleCopy = async (description, index) => {
        try {
            await navigator.clipboard.writeText(description);
            setCopiedIndex(index);
            message.success("Copied!", 1);
            setTimeout(() => setCopiedIndex(null), 2000);
        } catch {
            message.error("Failed to copy");
        }
    };

    const handleSelect = (description) => {
        onSelect(description);
        setCopiedIndex(null);
        onClose();
    };

    const renderFooter = () => {
        if (localGenerating) {
            return <Button onClick={onClose}>Close</Button>;
        }

        if (suggestions.length > 0) {
            return (
                <Space>
                    <Button onClick={onClose}>Close</Button>
                    <Button icon={<ReloadOutlined />} onClick={handleGenerate} disabled={!tagName} type={hasParamsChanged ? "primary" : "default"}>
                        Regenerate
                    </Button>
                </Space>
            );
        }

        return (
            <Space>
                <Button onClick={onClose} disabled={localGenerating}>
                    Close
                </Button>
                <Button type="primary" icon={<RobotOutlined />} onClick={handleGenerate} disabled={!tagName}>
                    Generate
                </Button>
            </Space>
        );
    };

    return (
        <Modal
            open={visible}
            title="AI Description Suggestions"
            onCancel={localGenerating ? undefined : onClose} // Disable c
            width={600}
            centered
            maskClosable={false}
            destroyOnHidden
            closable={!localGenerating} // Hide X button when generating
            keyboard={!localGenerating}
            footer={renderFooter()}
        >
            <Space orientation="vertical" style={{ width: "100%" }} size="middle">
                <div style={{ padding: "8px 12px", backgroundColor: "#f5f5f5", borderRadius: "6px" }}>
                    <Text type="secondary" className="text-base font-semibold">
                        <strong>{tagName || "No tag name"}</strong>
                        {businessAreaName && <span> • {businessAreaName}</span>}
                    </Text>
                </div>

                {hasParamsChanged && suggestions.length > 0 && (
                    <Alert
                        title={
                            <span className="font-light italic">
                                Name or business area has changed since last generation.
                                <Button type="link" size="small" onClick={handleGenerate} disabled={!tagName || localGenerating}>
                                    Regenerate now
                                </Button>
                            </span>
                        }
                        type="warning"
                        showIcon
                        banner
                        icon={<InfoCircleOutlined style={{ fontSize: "14px" }} />}
                    />
                )}

                {localGenerating && (
                    <div style={{ textAlign: "center", padding: "32px 0" }}>
                        <Spin size="large" tip="Generating AI suggestions..." />
                    </div>
                )}

                {!localGenerating && suggestions.length === 0 && <Empty description="No suggestions yet" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "32px 0" }} />}

                {!localGenerating && suggestions.length > 0 && (
                    <List
                        dataSource={suggestions}
                        renderItem={(suggestion, index) => (
                            <List.Item
                                style={{
                                    padding: "12px 16px",
                                    border: "1px solid #e8e8e8",
                                    borderRadius: "6px",
                                    marginBottom: "8px",
                                    backgroundColor: "#fafafa",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                    e.target.closest(".ant-list-item").style.backgroundColor = "#f0f9ff";
                                    e.target.closest(".ant-list-item").style.borderColor = "#91c7f5";
                                }}
                                onMouseLeave={(e) => {
                                    e.target.closest(".ant-list-item").style.backgroundColor = "#fafafa";
                                    e.target.closest(".ant-list-item").style.borderColor = "#e8e8e8";
                                }}
                                actions={[
                                    <Button
                                        key="copy"
                                        type="default"
                                        size="small"
                                        icon={copiedIndex === index ? <CheckOutlined style={{ color: "#52c41a" }} /> : <CopyOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(suggestion, index);
                                        }}
                                        style={{ minWidth: "60px" }}
                                    >
                                        {copiedIndex === index ? "Copied" : "Copy"}
                                    </Button>,
                                    <Button
                                        key="select"
                                        type="primary"
                                        size="small"
                                        icon={<CheckCircleOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelect(suggestion);
                                        }}
                                    >
                                        Use This
                                    </Button>,
                                ]}
                            >
                                <div style={{ flex: 1 }}>
                                    <Text
                                        style={{
                                            fontSize: "14px",
                                            lineHeight: "1.4",
                                            display: "block",
                                            color: "#262626",
                                        }}
                                    >
                                        {suggestion}
                                    </Text>
                                </div>
                            </List.Item>
                        )}
                    />
                )}
            </Space>
        </Modal>
    );
};

export default AIDescriptionGenerator;
